import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { api, formatPrice } from "@/lib/api";
import { estadoInfo, interesLabel, lugarLabel } from "@/components/closefriends/constants";

const Kpi = ({ label, value, testId }) => (
  <div className="rounded-2xl bg-white border border-[#E3E7E2] p-4">
    <p className="text-xs text-muted-moss">{label}</p>
    <p data-testid={testId} className="font-display text-2xl font-bold text-charcoal mt-1">{value}</p>
  </div>
);

const Bars = ({ title, data, labelFn = (k) => k, testId }) => {
  const entries = Object.entries(data || {}).sort((a, b) => b[1] - a[1]);
  const max = Math.max(1, ...entries.map((e) => e[1]));
  return (
    <div data-testid={testId} className="rounded-2xl bg-white border border-[#E3E7E2] p-4">
      <p className="font-bold text-charcoal mb-3 text-sm">{title}</p>
      {entries.length === 0 ? <p className="text-xs text-muted-moss">Sin datos</p> : entries.map(([k, v]) => (
        <div key={k} className="mb-2">
          <div className="flex justify-between text-xs text-charcoal"><span>{labelFn(k)}</span><b>{v}</b></div>
          <div className="h-2 rounded-full bg-cream-2 mt-1"><div className="h-2 rounded-full bg-coral" style={{ width: `${(v / max) * 100}%` }} /></div>
        </div>
      ))}
    </div>
  );
};

export const CloseFriendsStats = () => {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/close-friends/stats").then(({ data }) => setS(data)); }, []);
  if (!s) return <div className="grid place-items-center py-16"><Loader2 className="w-7 h-7 animate-spin text-coral" /></div>;

  return (
    <div className="space-y-4" data-testid="cf-stats">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <Kpi label="Contactos" value={s.total_contactos} testId="cf-kpi-contactos" />
        <Kpi label="Perros" value={s.perros} testId="cf-kpi-perros" />
        <Kpi label="Gatos" value={s.gatos} testId="cf-kpi-gatos" />
        <Kpi label="Alimentos registrados" value={s.total_alimentos} testId="cf-kpi-alimentos" />
        <Kpi label="Precio promedio" value={formatPrice(s.precio_promedio)} testId="cf-kpi-precio" />
        <Kpi label="Precio prom. por kg" value={formatPrice(s.precio_kg_promedio)} testId="cf-kpi-precio-kg" />
      </div>

      <div className="rounded-2xl bg-white border border-[#E3E7E2] overflow-x-auto" data-testid="cf-top-marcas">
        <p className="font-bold text-charcoal text-sm p-4 pb-2">Top marcas</p>
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase text-muted-moss"><tr>{["#", "Marca", "Menciones", "Perro / Gato", "Precio prom.", "₡/kg prom."].map((h) => <th key={h} className="px-4 py-2 whitespace-nowrap">{h}</th>)}</tr></thead>
          <tbody>
            {s.top_marcas.length === 0 && <tr><td colSpan={6} className="px-4 py-6 text-center text-muted-moss">Sin datos</td></tr>}
            {s.top_marcas.map((b, i) => (
              <tr key={b.marca} className="border-t border-[#E3E7E2]">
                <td className="px-4 py-2 text-muted-moss">{i + 1}</td>
                <td className="px-4 py-2 font-bold">{b.marca}</td>
                <td className="px-4 py-2">{b.menciones}</td>
                <td className="px-4 py-2">{b.perro} / {b.gato}</td>
                <td className="px-4 py-2 whitespace-nowrap">{formatPrice(b.precio_promedio)}</td>
                <td className="px-4 py-2 whitespace-nowrap">{formatPrice(b.precio_kg_promedio)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Bars title="Interés en prueba Lenda" data={s.interes} labelFn={interesLabel} testId="cf-bars-interes" />
        <Bars title="Estado de contactos" data={s.estados} labelFn={(k) => estadoInfo(k).label} testId="cf-bars-estados" />
        <Bars title="Lugar de compra" data={s.lugares} labelFn={lugarLabel} testId="cf-bars-lugares" />
        <Bars title="Zonas principales" data={s.zonas} testId="cf-bars-zonas" />
      </div>
    </div>
  );
};
